import { OperatorType } from 'bones/utils/queryBuilder/constants';
import { DraftExpression } from 'bones/utils/queryBuilder/types';

export const assignSubGraphs = () => {
  const processedIds = new Set<string>();
  const subGraphs: Array<Set<string>> = [];

  const processNewId = (id?: string) => {
    // skip if there is no id, or the id is already processed
    if (!id || processedIds.has(id)) {
      return;
    }

    // mark the id as processed
    processedIds.add(id);
    // add the id to a new sub-graph
    subGraphs.push(new Set([id]));
  };

  const assignSubGraph = (idA?: string, idB?: string) => {
    if (idA === idB || !idA || !idB) {
      // skip lookup if the ids are the same, or we are missing an id
      processNewId(idA);
      processNewId(idB);
      return;
    }

    const idxA = subGraphs.findIndex((subGraph) => subGraph.has(idA));
    const idxB = subGraphs.findIndex((subGraph) => subGraph.has(idB));

    if (idxA === -1 && idxB === -1) {
      // create a new sub-graph if there is not one yet for these ids
      const combinedSubGraph = new Set([idA, idB]);
      subGraphs.push(combinedSubGraph);
      processedIds.add(idA);
      processedIds.add(idB);
    } else if (idxA === idxB) {
      // skip if the ids are already in the same graph
    } else if (idxA > -1 && idxB > -1) {
      // combine existing subgraphs
      const combinedSubGraph = new Set([
        ...subGraphs[idxA],
        ...subGraphs[idxB],
      ]);
      [idxA, idxB]
        .sort()
        .reverse()
        .forEach((idx) => {
          subGraphs.splice(idx, 1);
        });
      subGraphs.push(combinedSubGraph);
    } else if (idxA > -1) {
      // add the unprocessed id to the existing sub-graph
      subGraphs[idxA].add(idB);
      processedIds.add(idB);
    } else if (idxB > -1) {
      // add the unprocessed id to the existing sub-graph
      subGraphs[idxB].add(idA);
      processedIds.add(idA);
    }
  };

  return { assignSubGraph, subGraphs };
};

export const subGraphsValidator = (expressions: DraftExpression[]) => {
  let firstVariableId: string | undefined;
  const assignFirstVariableId = (id?: string) => {
    if (!firstVariableId && id) {
      firstVariableId = id;
    }
  };

  const { subGraphs, assignSubGraph } = assignSubGraphs();
  expressions.forEach((expression) =>
    expression.filters.forEach((filter) => {
      // always process the subject of a filter
      assignFirstVariableId(filter.subject?.id);
      assignSubGraph(filter.subject?.id);

      if (
        filter.operator?.operatorType === OperatorType.DATATYPE &&
        filter.operator.comparison
      ) {
        assignFirstVariableId(filter.comparisonSubject?.id);
        assignSubGraph(filter.subject?.id, filter.comparisonSubject?.id);
      } else if (
        filter.operator?.operatorType === OperatorType.OBJECT &&
        typeof filter.value !== 'string'
      ) {
        assignFirstVariableId(filter.value?.id);
        assignSubGraph(filter.subject?.id, filter.value?.id);
      }
    })
  );

  const firstSubGraph = subGraphs.find(
    (subGraph) => firstVariableId && subGraph.has(firstVariableId)
  );

  return (id?: string): boolean => {
    if (!id) {
      return true;
    }

    return Boolean(
      // istanbul ignore next
      firstSubGraph?.has(id)
    );
  };
};
